import React from 'react';
import { Title, MutedText } from '../../../account/account-settings.styled';
import { Input } from '../../../profile/profile.styled';
import { VSpace } from '../../../account/account-settings';
import { Divider } from '../../billing.styled';
import CountrySelect from '../../country-select/country-select';
import {
  BillingDetailsLabel,
  ValidationMessage,
  ModalOverlay,
  ModalContent,
  ModalActions,
  DiscardButton,
  SaveButton,
  // Overlay,
  // ModalShell,
  // EditDialog
} from './edit-details.styled';
import { BillingDetails, profileApi } from '../../../../../../api';
import * as RadixDialog from '@radix-ui/react-dialog';
import { useEditDetailsModal } from './edit-details.hooks';

export type EditDetailsModalProps = {
  open: boolean;
  initial: BillingDetails;
  onClose: () => void;
  onSave: (details: BillingDetails) => void;
  onNavigateToCheckout?: () => void;
  checkoutFlow?: boolean;
  profile?: any;
};

const EditDetailsModal: React.FC<EditDetailsModalProps> = (props) => {
  const { open, checkoutFlow = false, profile } = props;

  const {
    // panelRef,
    details,
    setDetails,
    errors,
    setErrors,
    saving,
    showUnsavedModal,
    setShowUnsavedModal,
    handleCloseAttempt,
    handleDiscardChanges,
    handleSaveChanges,
    handleSave,
  } = useEditDetailsModal(props);

  // Load profile for pre-fill if needed (runs once when modal opens)
  const hasPrefilled = React.useRef(false);
  React.useEffect(() => {
    if (!open) { hasPrefilled.current = false; return; }
    if (hasPrefilled.current) return;
    if (!details.firstName || !details.lastName || !details.companyName) {
      hasPrefilled.current = true;
      loadProfileForPrefill();
    }
  }, [open]);

  const loadProfileForPrefill = async () => {
    if (profile) {
      // Use passed profile data
      setDetails((prev) => ({
        ...prev,
        firstName: prev.firstName || profile.first_name || '',
        lastName: prev.lastName || profile.last_name || '',
        companyName: prev.companyName || profile.company_name || '',
        country: prev.country || 'United States',
      }));
    } else {
      // Fallback to API call if profile not provided
      try {
        const profileData = await profileApi.get();
        setDetails((prev) => ({
          ...prev,
          firstName: prev.firstName || profileData.first_name || '',
          lastName: prev.lastName || profileData.last_name || '',
          companyName: prev.companyName || profileData.company_name || '',
          country: prev.country || 'United States',
        }));
      } catch (error) {
        console.error('Failed to load profile for pre-fill:', error);
      }
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        boxSizing: 'border-box',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
      }}
    >
      {/* Header with only logo */}
      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px',
          paddingTop: 'calc(20px + env(safe-area-inset-top, 0px))',
          position: 'sticky',
          top: 0,
          background: '#1A1A1C',
          zIndex: 1,
          flexShrink: 0,
        }}
      >
        <img
          onClick={handleCloseAttempt}
          title="Back"
          data-testid="settings-back"
          src="/settings-back.svg"
          alt="Back"
          width={44}
          height={44}
          style={{ display: 'block', cursor: 'pointer' }}
        />
        <div style={{ width: "100%" , display: 'flex', justifyContent: 'center' }}>
        <img src="/main-logo-white.svg" alt="logo" width={124} height="auto" draggable={false} />
        </div>
        <div style={{ width: "44px" }}></div>
      </header>

      {/* Content */}
      <div>
        <div style={{ padding: '20px' }}>
          <Title>Billing details</Title>
          <VSpace $h={16} />

          <div style={{ display: 'flex', gap: '16px', flexDirection: 'column' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="firstName">First name*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.firstName
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="firstName"
                value={details.firstName}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, firstName: e.target.value }));
                  if (errors.firstName)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.firstName;
                      return newErrors;
                    });
                }}
              />
              {errors.firstName && <ValidationMessage>{errors.firstName}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="lastName">Last name*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.lastName
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="lastName"
                value={details.lastName}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, lastName: e.target.value }));
                  if (errors.lastName)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.lastName;
                      return newErrors;
                    });
                }}
              />
              {errors.lastName && <ValidationMessage>{errors.lastName}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="companyName">Company*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.companyName
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="companyName"
                value={details.companyName}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, companyName: e.target.value }));
                  if (errors.companyName)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.companyName;
                      return newErrors;
                    });
                }}
              />
              {errors.companyName && <ValidationMessage>{errors.companyName}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="streetAddress">Street address*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.streetAddress
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="streetAddress"
                value={details.streetAddress}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, streetAddress: e.target.value }));
                  if (errors.streetAddress)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.streetAddress;
                      return newErrors;
                    });
                }}
              />
              {errors.streetAddress && (
                <ValidationMessage>{errors.streetAddress}</ValidationMessage>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="city">City*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.city ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' } : {}),
                }}
                id="city"
                value={details.city}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, city: e.target.value }));
                  if (errors.city)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.city;
                      return newErrors;
                    });
                }}
              />
              {errors.city && <ValidationMessage>{errors.city}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="stateProvince">State / province*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.stateProvince
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="stateProvince"
                value={details.stateProvince}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, stateProvince: e.target.value }));
                  if (errors.stateProvince)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.stateProvince;
                      return newErrors;
                    });
                }}
                placeholder="Select State/Province"
              />
              {errors.stateProvince && (
                <ValidationMessage>{errors.stateProvince}</ValidationMessage>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="postalCode">Postal code*</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                  ...(errors.postalCode
                    ? { outline: '2px #D65F46 solid', outlineOffset: '-2px' }
                    : {}),
                }}
                id="postalCode"
                value={details.postalCode}
                onChange={(e) => {
                  setDetails((prev) => ({ ...prev, postalCode: e.target.value }));
                  if (errors.postalCode)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.postalCode;
                      return newErrors;
                    });
                }}
              />
              {errors.postalCode && <ValidationMessage>{errors.postalCode}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="country">Country*</BillingDetailsLabel>
              <CountrySelect
                id="country"
                label="Country"
                value={details.country}
                onChange={(value) => {
                  setDetails((prev) => ({ ...prev, country: value }));
                  if (errors.country)
                    setErrors((prev) => {
                      const newErrors = { ...prev };
                      delete newErrors.country;
                      return newErrors;
                    });
                }}
                error={!!errors.country}
                placeholder="Select country"
              />
              {errors.country && <ValidationMessage>{errors.country}</ValidationMessage>}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <BillingDetailsLabel htmlFor="vatTaxId">VAT / tax ID</BillingDetailsLabel>
              <Input
                style={{
                  fontSize: '14px',
                  fontWeight: 400,
                }}
                id="vatTaxId"
                value={details.vatTaxId}
                onChange={(e) => setDetails((prev) => ({ ...prev, vatTaxId: e.target.value }))}
              />
            </div>

            <Divider />

            <MutedText style={{ fontSize: '12px', lineHeight: 'normal', fontWeight: '400' }}>
              These details appear on your invoices and are used for tax purposes.
            </MutedText>
          </div>
          {/* </div> */}
          <VSpace $h={32} />
          {/* Bottom buttons */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: '16px',
            }}
          >
            <button
              style={{
                display: 'flex',
                height: '44px',
                width: '100%',
                background: '#5E84ED',
                borderRadius: '12px',
                justifyContent: 'center',
                alignItems: 'center',
                color: 'white',
                fontSize: '15px',
                fontFamily: 'Inter',
                fontWeight: 600,
                lineHeight: '21px',
                cursor: 'pointer',
                outline: 'none',
                border: 'none',
              }}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : checkoutFlow ? 'Continue' : 'Save billing details'}
            </button>
            <div
              style={{
                height: '44px',
                width: '100%',
                background: 'transparent',
                borderRadius: '12px',
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
              }}
            >
              <button
                style={{
                  color: '#B7BDCB',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '15px',
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  lineHeight: '21px',
                }}
                onClick={handleCloseAttempt}
              >
                {checkoutFlow ? 'Go back' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Unsaved Changes Modal */}
      <RadixDialog.Root open={showUnsavedModal} onOpenChange={setShowUnsavedModal}>
        <RadixDialog.Portal>
          <ModalOverlay />
          <ModalContent aria-describedby={undefined}>
            <RadixDialog.DialogTitle style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
              Save billing details
            </RadixDialog.DialogTitle>
            <Title>Save billing details?</Title>
            <ModalActions>
              <RadixDialog.Close asChild>
                <DiscardButton onClick={handleDiscardChanges}>Discard</DiscardButton>
              </RadixDialog.Close>
              <SaveButton onClick={handleSaveChanges} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </SaveButton>
            </ModalActions>
          </ModalContent>
        </RadixDialog.Portal>
      </RadixDialog.Root>
    </div>
  );
};

export default EditDetailsModal;
